import { motion } from "framer-motion";
import { HandHeart, Users, Handshake, Heart } from "lucide-react";
import { Link } from "react-router-dom";
import { IMAGES } from "../data/content";

const easing = [0.16, 1, 0.3, 1];
const parent = { hidden: {}, show: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } } };
const child = { hidden: { y: "118%" }, show: { y: "0%", transition: { duration: 0.95, ease: easing } } };
const fade = { hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: easing } } };

function Line({ children, className = "", wrap = "pb-[0.08em]" }) {
  return (
    <span className={`block overflow-hidden ${wrap}`}>
      <motion.span variants={child} className={`block ${className}`}>{children}</motion.span>
    </span>
  );
}

const KenBurns = ({ className }) => (
  <motion.img
    src={IMAGES.heroTeens}
    alt="Young people supported by Thefrent Foundation"
    className={className}
    initial={{ opacity: 0, scale: 1.08 }}
    animate={{ opacity: 1, scale: [1.08, 1.16] }}
    transition={{
      opacity: { duration: 1.3, ease: easing },
      scale: { duration: 20, ease: "easeInOut", repeat: Infinity, repeatType: "reverse" },
    }}
  />
);

export function Hero() {
  return (
    <section className="relative flex flex-col justify-center overflow-hidden brand-ink text-white lg:min-h-[92vh]" data-testid="hero">
      <div className="grain absolute inset-0 z-[1] opacity-30" />
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -left-40 top-10 z-[1] h-[36rem] w-[36rem] rounded-full bg-[#c8892b]/10 blur-[130px]"
        animate={{ opacity: [0.5, 0.85, 0.5] }}
        transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Desktop full-bleed cinematic image */}
      <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-[64%] overflow-hidden lg:block">
        <KenBurns className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0d1d47] via-[#0d1d47]/35 to-transparent" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(115% 100% at 62% 42%, transparent 55%, rgba(9,18,43,0.5) 100%)" }} />
        <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-[#0d1d47] to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-[#0d1d47] to-transparent" />
      </div>

      {/* Copy */}
      <div className="relative z-10 mx-auto w-full max-w-[1400px] px-5 pb-16 pt-32 md:px-8 lg:pb-28 lg:pt-40">
        <div className="lg:w-[52%] lg:pl-2">
          <motion.p
            variants={fade}
            initial="hidden"
            animate="show"
            transition={{ delay: 0.15 }}
            className="mb-4 text-xs font-medium uppercase tracking-[0.24em] text-white/55 sm:text-sm"
            data-testid="hero-org-name"
          >
            Thefrent Foundation Inc.
          </motion.p>
          <motion.h1
            variants={parent}
            initial="hidden"
            animate="show"
            className="font-heading text-5xl font-extrabold uppercase leading-[0.94] tracking-tight sm:text-6xl lg:text-7xl"
          >
            <Line>Building</Line>
            <Line>Brighter Futures</Line>
            <Line wrap="pb-6 -mb-2" className="mt-3 font-script text-6xl font-bold normal-case leading-[1.08] tracking-normal txt-gold sm:text-7xl lg:text-[5.5rem]">
              Together.
            </Line>
          </motion.h1>

          <motion.div variants={fade} initial="hidden" animate="show" transition={{ delay: 0.55 }} className="mt-6">
            <Heart className="h-6 w-6 txt-gold" />
          </motion.div>

          <motion.p variants={fade} initial="hidden" animate="show" transition={{ delay: 0.65 }} className="mt-6 max-w-md text-lg font-medium leading-relaxed text-white/85">
            Empowering people. Advancing communities. Creating opportunity.
          </motion.p>

          <motion.div variants={fade} initial="hidden" animate="show" transition={{ delay: 0.8 }} className="mt-9 flex flex-nowrap items-stretch gap-2.5 sm:gap-3">
            <Link to="/donate" data-testid="hero-donate" className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md brand-gold-bg px-3 py-2.5 text-[11px] font-bold uppercase tracking-wide text-[#0d1d47] transition-transform hover:-translate-y-0.5 sm:px-6 sm:py-3.5 sm:text-sm">
              <HandHeart className="hidden h-4 w-4 sm:inline" /> <span className="sm:hidden">Donate</span><span className="hidden sm:inline">Donate Now</span>
            </Link>
            <Link to="/get-involved?tab=volunteer" data-testid="hero-volunteer" className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md border border-white/30 px-3 py-2.5 text-[11px] font-bold uppercase tracking-wide text-white transition-colors hover:border-[#c8892b] hover:text-[#e0b25a] sm:px-6 sm:py-3.5 sm:text-sm">
              <Users className="hidden h-4 w-4 sm:inline" /> Volunteer
            </Link>
            <Link to="/get-involved?tab=partner" data-testid="hero-partner" className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md border border-white/30 px-3 py-2.5 text-[11px] font-bold uppercase tracking-wide text-white transition-colors hover:border-[#c8892b] hover:text-[#e0b25a] sm:px-6 sm:py-3.5 sm:text-sm">
              <Handshake className="hidden h-4 w-4 sm:inline" /> <span className="sm:hidden">Partner</span><span className="hidden sm:inline">Partner With Us</span>
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Mobile: full-width tapered white accent line above image */}
      <div className="relative z-10 mt-2 h-3 w-full lg:hidden" aria-hidden>
        <div
          className="h-full w-full bg-white"
          style={{ clipPath: "polygon(0 44%, 100% 0, 100% 100%, 0 56%)" }}
        />
      </div>

      {/* Mobile image with wave rising directly from its base (no navy gap) */}
      <div className="relative z-10 h-72 w-full overflow-hidden lg:hidden">
        <KenBurns className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-x-0 bottom-0 z-10 leading-[0]">
          <svg viewBox="0 0 1440 120" preserveAspectRatio="none" className="block h-[70px] w-full md:h-[100px]">
            <path d="M0,64 C360,120 720,10 1080,40 C1260,55 1380,80 1440,72 L1440,120 L0,120 Z" fill="#f4f1ea" />
            <path d="M0,54 C360,110 720,0 1080,30 C1260,45 1380,70 1440,62" fill="none" stroke="#c8892b" strokeWidth="3" />
          </svg>
        </div>
      </div>

      {/* Wave transition to cream (desktop) */}
      <div className="relative z-10 -mt-1 hidden w-full leading-[0] lg:block">
        <svg viewBox="0 0 1440 120" preserveAspectRatio="none" className="block h-[110px] w-full">
          <path d="M0,64 C360,120 720,10 1080,40 C1260,55 1380,80 1440,72 L1440,120 L0,120 Z" fill="#f4f1ea" />
          <path d="M0,54 C360,110 720,0 1080,30 C1260,45 1380,70 1440,62" fill="none" stroke="#c8892b" strokeWidth="3" />
        </svg>
      </div>
    </section>
  );
}
